import { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, X, Search } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage } from '@/lib/api';

// PR-RIYADH-FIX-2 MAY 12 — searchable client dropdown with inline "+ Add new
// client" that POSTs and re-fetches before selecting. Trip form needs this
// because Riyadh gets walk-up clients daily and the full Clients page is too
// many fields for that step.

export interface ClientOption {
  id: string;
  code?: string | null;
  nameEn: string;
  nameAr?: string | null;
}

interface Props {
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  className?: string;
  // PR-PERMS-CLIENTS MAY 12 — restrict the dropdown to one or more scopes.
  // E.g. ['riyadh','all'] hides NEOM-only clients in the Riyadh trip form.
  // Omit to show every scope.
  scopes?: Array<'neom' | 'riyadh' | 'all'>;
  // When creating a new client inline, pre-fill these.
  createDefaults?: {
    projectScope?: 'neom' | 'riyadh' | 'all';
    isOneTime?: boolean;
  };
}

interface ListResponse {
  items: Array<{
    id: string;
    code: string | null;
    nameEn?: string | null;
    name_en?: string | null;
    nameAr?: string | null;
    name_ar?: string | null;
  }>;
}

function normalize(items: ListResponse['items']): ClientOption[] {
  return items.map((c) => ({
    id: c.id,
    code: c.code ?? null,
    nameEn: c.nameEn ?? c.name_en ?? c.code ?? c.id.slice(0, 8),
    nameAr: c.nameAr ?? c.name_ar ?? null,
  }));
}

export function ClientPickerOrCreate({
  value, onChange, placeholder = 'Search clients…', className,
  scopes, createDefaults,
}: Props) {
  const [items, setItems] = useState<ClientOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newVat, setNewVat] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newIsOneTime, setNewIsOneTime] = useState<boolean>(createDefaults?.isOneTime ?? false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scopeQs = scopes && scopes.length > 0 ? `&projectScope=${scopes.join(',')}` : '';

  async function refresh() {
    setLoading(true);
    try {
      const r = await apiClient.get<ListResponse>(`/api/v1/clients?pageSize=200${scopeQs}`);
      setItems(normalize(r.data.items ?? []));
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, [scopeQs]);
  useEffect(() => { setNewIsOneTime(createDefaults?.isOneTime ?? false); }, [createDefaults?.isOneTime]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((c) =>
      c.nameEn.toLowerCase().includes(q)
      || (c.nameAr ?? '').toLowerCase().includes(q)
      || (c.code ?? '').toLowerCase().includes(q));
  }, [items, query]);

  const selected = items.find((c) => c.id === value) ?? null;

  async function submitCreate() {
    if (!newName.trim()) { setError('Name required'); return; }
    setError(null);
    setCreating(true);
    try {
      const r = await apiClient.post<{ client: ClientOption & { nameEn: string } }>('/api/v1/clients', {
        nameEn: newName.trim(),
        primaryContactPhone: newPhone.trim() || undefined,
        vatNumber: newVat.trim() || undefined,
        notes: newNotes.trim() || undefined,
        projectScope: createDefaults?.projectScope,
        isOneTime: newIsOneTime,
      });
      const created = r.data.client;
      setItems((prev) => [...prev, {
        id: created.id, code: created.code ?? null, nameEn: created.nameEn, nameAr: created.nameAr ?? null,
      }].sort((a, b) => a.nameEn.localeCompare(b.nameEn)));
      onChange(created.id);
      setShowCreate(false);
      setOpen(false);
      setNewName(''); setNewPhone(''); setNewVat(''); setNewNotes('');
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className={'relative ' + (className ?? '')}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-11 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-base text-left"
      >
        <span className={selected ? '' : 'text-muted-foreground'}>
          {selected ? (selected.nameEn + (selected.code ? ` · ${selected.code}` : '')) : placeholder}
        </span>
        <span className="text-muted-foreground text-xs">▾</span>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-md border border-border bg-card shadow-lg max-h-[380px] flex flex-col">
          <div className="p-2 border-b border-border flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to filter…"
              className="flex-1 bg-transparent outline-none text-sm"
            />
            {value && (
              <button type="button" onClick={() => { onChange(null); setOpen(false); }} className="text-xs text-muted-foreground hover:text-foreground">
                Clear
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-3 text-sm text-muted-foreground inline-flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-3 text-sm text-muted-foreground">No clients match.</div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => { onChange(c.id); setOpen(false); setQuery(''); }}
                  className={'w-full text-left px-3 py-2 hover:bg-muted/40 text-sm flex items-center justify-between '
                    + (c.id === value ? 'bg-muted/30' : '')}
                >
                  <span>{c.nameEn}{c.nameAr ? ` · ${c.nameAr}` : ''}</span>
                  {c.code && <span className="text-xs text-muted-foreground font-mono">{c.code}</span>}
                </button>
              ))
            )}
          </div>

          {!showCreate ? (
            <div className="p-2 border-t border-border">
              <Button type="button" variant="outline" size="sm" className="w-full gap-2" onClick={() => { setShowCreate(true); setNewName(query); }}>
                <Plus className="h-3.5 w-3.5" /> Add new client{query ? ` "${query}"` : ''}
              </Button>
            </div>
          ) : (
            <div className="p-3 border-t border-border space-y-2 bg-muted/10">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">New client</div>
                <button type="button" onClick={() => setShowCreate(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="h-3 w-3" />
                </button>
              </div>
              <div className="space-y-2">
                <Field label="Name (required)">
                  <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Client name" />
                </Field>
                <Field label="Phone (optional)">
                  <Input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="+966…" />
                </Field>
                <Field label="VAT number (optional)">
                  <Input value={newVat} onChange={(e) => setNewVat(e.target.value)} placeholder="3xxxxxxxxxxx003" />
                </Field>
                <Field label="Notes (optional)">
                  <textarea
                    rows={2}
                    value={newNotes}
                    onChange={(e) => setNewNotes(e.target.value)}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                  />
                </Field>
                {createDefaults?.projectScope === 'riyadh' && (
                  <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newIsOneTime}
                      onChange={(e) => setNewIsOneTime(e.target.checked)}
                    />
                    One-time client (recommended for Riyadh walk-ups)
                  </label>
                )}
                {error && <div className="text-xs text-rose-600 dark:text-rose-400">{error}</div>}
                <div className="flex gap-2">
                  <Button type="button" size="sm" onClick={submitCreate} disabled={creating || !newName.trim()} className="gap-2">
                    {creating && <Loader2 className="h-3 w-3 animate-spin" />} Save & use
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
